import { Promotion } from '@/@type/promotion';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { RootState, useSelector } from '@/redux/store';
import { Icon } from '@iconify/react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, FormControl, IconButton, MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import { FormikProps, getIn } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '../styles';
import { getPromoIds } from '../tools';
interface PromoTypeProp {
  formik: FormikProps<Promotion>;
  promoValidator?: (promo: Promotion) => boolean;
  formikPath?: string;
  label?: string;
  tooltipText?: string;
  multiple?: boolean;
}

export const PromoSelector = (props: PromoTypeProp): JSX.Element => {
  const { t } = useTranslation();
  const { formik, promoValidator, label, formikPath = 'resultingPromotions', tooltipText, multiple = true } = props;
  const { setFieldValue, values, touched, errors } = formik;
  const { validPromotions, isLoading } = useSelector((state: RootState) => state.promotion);
  const [selectedPromo, setSelectedPromo] = useState<Promotion[]>([]);
  const { showSnackbar } = useSnackbarHelper();

  // Use useMemo to memoize initialSelectedPromoIds to avoid recomputation on each render
  const initialSelectedPromoIds = useMemo(
    () => getIn(values, formikPath) || [],
    [values, formikPath]
  );

  const getSelectedPromoIds = getPromoIds(selectedPromo);

  // Update selectedPromo state when initial values change or validPromotions are loaded
  useEffect(() => {
    if (initialSelectedPromoIds.length && validPromotions.length) {
      const initialSelectedPromos = validPromotions.filter((promo) =>
        initialSelectedPromoIds.includes(promo.id)
      );
      setSelectedPromo(initialSelectedPromos);
    }
  }, [initialSelectedPromoIds, validPromotions]);

  const handlePromoChange = useCallback(
    (event: SelectChangeEvent<string | string[]>) => {
      // Handle both single and multiple selection cases
      const value = event.target.value;
      const selectedPromoIds = multiple
        ? (value as string[])
        : [value as string];

      const selectedPromoList = validPromotions.filter((promo) =>
        selectedPromoIds.includes(promo.id)
      );

      const isPromoTypeValid = selectedPromoList.every((promo) => {
        if (!promoValidator) {
          return true;
        }
        return promoValidator(promo);
      });

      if (!isPromoTypeValid) {
        showSnackbar('The selected promotion type is not allowed.', 'error');
        return;
      }

      setSelectedPromo(selectedPromoList);

      // When multiple is false, set the single ID instead of an array
      if (multiple) {
        const ids = getPromoIds(selectedPromoList);
        setFieldValue(formikPath, ids);
      } else {
        const id = selectedPromoList.length > 0 ? selectedPromoList[0].id : '';
        setFieldValue(formikPath, id);
      }
    },
    [validPromotions, formikPath, showSnackbar, setFieldValue, promoValidator, multiple]
  );

  return (
    <FormControl sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      height: '100%',
    }}>
      {label && (
        <Label>
          {t(label)}
          {tooltipText && (
            <Tooltip title={t(tooltipText)}>
              <IconButton>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Label>
      )
      }
      <Select
        name={formikPath}
        multiple={multiple}
        sx={{ width: '100%' }}
        value={getSelectedPromoIds}
        onChange={handlePromoChange}
        error={Boolean(getIn(touched, formikPath) && getIn(errors, formikPath))}
      >
        <MenuItem value="placeholder" disabled>
          Select Promotions
        </MenuItem>
        {isLoading ? (
          <MenuItem value="placeholder" disabled>
            Loading...
          </MenuItem>
        ) : (
          validPromotions.map(
            (promo) =>
              (!promoValidator || promoValidator(promo)) &&
              promo && (
                <MenuItem
                  value={promo.id}
                  key={promo.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  disabled={promo.status === 'draft'}
                >
                  <Box component="span" sx={{ flexGrow: 1 }}>
                    {promo.name} {promo.status === 'draft' && ' [draft]'}
                  </Box>
                  {selectedPromo.some((sPromo) => sPromo.id === promo.id) && (
                    <Icon icon="material-symbols:close" />
                  )}
                </MenuItem>
              )
          )
        )}
      </Select>
    </FormControl >
  );
};
